import { Temporal } from "temporal-polyfill";

// Timezone type matching the backend enum
export type TimeZone =
  | "America/New_York"
  | "America/Chicago"
  | "America/Denver"
  | "America/Los_Angeles"
  | "America/Anchorage"
  | "America/Adak"
  | "Pacific/Honolulu"
  | "America/Phoenix";

// Human-readable timezone labels
const TIMEZONE_LABELS: Record<TimeZone, { full: string; short: string }> = {
  "America/New_York": { full: "Eastern Time", short: "ET" },
  "America/Chicago": { full: "Central Time", short: "CT" },
  "America/Denver": { full: "Mountain Time", short: "MT" },
  "America/Los_Angeles": { full: "Pacific Time", short: "PT" },
  "America/Anchorage": { full: "Alaska Time", short: "AKT" },
  "America/Adak": { full: "Hawaii-Aleutian Time", short: "HAT" },
  "Pacific/Honolulu": { full: "Hawaii Time", short: "HST" },
  "America/Phoenix": { full: "Arizona Time", short: "MST" },
};

/**
 * Get human-readable timezone label.
 */
export function getTimezoneLabel(timezoneId: string): {
  full: string;
  short: string;
} {
  return (
    TIMEZONE_LABELS[timezoneId as TimeZone] || {
      full: timezoneId,
      short: timezoneId,
    }
  );
}

// Default fallback timezone for error cases
const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Convert an ISO datetime string or Date to a ZonedDateTime in the specified timezone.
 * Falls back to America/New_York if timezone is invalid.
 */
export function toZonedDateTime(
  dateOrIso: Date | string,
  timezoneId: string,
): Temporal.ZonedDateTime {
  try {
    const instant =
      typeof dateOrIso === "string"
        ? Temporal.Instant.from(dateOrIso)
        : Temporal.Instant.fromEpochMilliseconds(dateOrIso.getTime());

    return instant.toZonedDateTimeISO(timezoneId);
  } catch (error) {
    console.error(
      `Invalid timezone "${timezoneId}", falling back to ${DEFAULT_TIMEZONE}:`,
      error,
    );
    const instant =
      typeof dateOrIso === "string"
        ? Temporal.Instant.from(dateOrIso)
        : Temporal.Instant.fromEpochMilliseconds(dateOrIso.getTime());

    return instant.toZonedDateTimeISO(DEFAULT_TIMEZONE);
  }
}

/**
 * Format a date in the specified timezone.
 * Returns a string like "2025-01-15" (YYYY-MM-DD).
 */
export function getDateInTimezone(
  dateOrIso: Date | string,
  timezoneId: string,
): string {
  const zdt = toZonedDateTime(dateOrIso, timezoneId);
  return zdt.toPlainDate().toString();
}

/**
 * Format a time in the specified timezone.
 * Returns a string like "09:30" (HH:mm).
 */
export function getTimeInTimezone(
  dateOrIso: Date | string,
  timezoneId: string,
): string {
  const zdt = toZonedDateTime(dateOrIso, timezoneId);
  const hour = zdt.hour.toString().padStart(2, "0");
  const minute = zdt.minute.toString().padStart(2, "0");
  return `${hour}:${minute}`;
}

/**
 * Combine a date string (YYYY-MM-DD) and time string (HH:mm) in the specified timezone
 * and return an ISO datetime string (UTC).
 *
 * DST handling:
 * - During "fall back" (when clocks go back), ambiguous times are resolved to the earlier occurrence
 * - During "spring forward" (when clocks skip), non-existent times are adjusted to the next valid time
 */
export function combineDateTimeInTimezone(
  dateStr: string,
  timeStr: string,
  timezoneId: string,
): string {
  if (!dateStr || !timeStr) {
    return "";
  }

  try {
    const [hours, minutes] = timeStr.split(":").map(Number);

    // Create a PlainDateTime from the date and time
    const plainDate = Temporal.PlainDate.from(dateStr);
    const plainDateTime = plainDate.toPlainDateTime({
      hour: hours,
      minute: minutes,
      second: 0,
    });

    // Convert to ZonedDateTime in the target timezone with explicit disambiguation
    // 'compatible' means: earlier during fall-back, push forward during spring-forward
    const zonedDateTime = plainDateTime.toZonedDateTime(timezoneId, {
      disambiguation: "compatible",
    });

    // Return as ISO string (UTC)
    return zonedDateTime.toInstant().toString();
  } catch (error) {
    // If timezone is invalid or other error, fall back to treating as local time
    console.error("Error converting datetime in timezone:", error);
    const [year, month, day] = dateStr.split("-").map(Number);
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    return date.toISOString();
  }
}

/**
 * Format a date for display in the specified timezone.
 * Example: "Wednesday, Jan 15"
 */
export function formatDateInTimezone(
  dateOrIso: Date | string,
  timezoneId: string,
): string {
  const zdt = toZonedDateTime(dateOrIso, timezoneId);
  const date = zdt.toPlainDate();

  // Get day of week name
  const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const dayOfWeek = dayNames[date.dayOfWeek - 1];

  // Get month name
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.month - 1];

  return `${dayOfWeek}, ${month} ${date.day}`;
}

/**
 * Format a time for display in the specified timezone.
 * Example: "9:30 AM"
 */
export function formatTimeInTimezone(
  dateOrIso: Date | string,
  timezoneId: string,
): string {
  const zdt = toZonedDateTime(dateOrIso, timezoneId);
  const hour = zdt.hour;
  const minute = zdt.minute;

  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  const displayMinute = minute.toString().padStart(2, "0");

  return `${displayHour}:${displayMinute} ${ampm}`;
}

type GetAvailableDatesInput = {
  startDate: Date | string;
  timezoneId: string;
  daysBefore?: number;
  daysAfter?: number;
};

/**
 * Get calendar-date options around a date in the specified timezone.
 */
export function getAvailableDates({
  startDate,
  timezoneId,
  daysBefore = 0,
  daysAfter = 2,
}: GetAvailableDatesInput): { value: string; label: string }[] {
  const zdt = toZonedDateTime(startDate, timezoneId);
  const dates: { value: string; label: string }[] = [];

  for (let i = -daysBefore; i <= daysAfter; i++) {
    const date = zdt.add({ days: i }).toPlainDate();
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    dates.push({
      value: date.toString(),
      label: `${dayNames[date.dayOfWeek - 1]}, ${monthNames[date.month - 1]} ${date.day}`,
    });
  }

  return dates;
}
